<template>
	<div>
		<div class="uk-width-1-1 uk-text-center" v-if="layout == 'article'">
			<div v-show="totalComments < 1 && !hideText" class="uk-margin-bottom">
			</div>
			<button v-show="buttonText && !loading && !loaded && !error" class="uk-button uk-button-primary uk-border-rounded" @click="handleClick">
    		{{ buttonText }}
  		</button>
		</div>
		<div v-if="layout == 'media' && !hideText" @click="handleClick" style="cursor: pointer">
			<span class="material-symbols-outlined"><svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path d="M240-400h480v-80H240v80Zm0-120h480v-80H240v80Zm0-120h480v-80H240v80ZM880-80 720-240H160q-33 0-56.5-23.5T80-320v-480q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v720ZM160-320h594l46 45v-525H160v480Zm0 0v-480 480Z"/></svg></span>
			<span v-show="totalComments > 0">&nbsp; <strong>{{ totalComments }}</strong> reactie <span v-show="totalComments > 1">s</span> tonen</span>
			<span v-show="totalComments == 0"> reageren</span>
		</div>
		<div>
			<div v-if="error" role="alert" class="uk-margin-top">
				<p>Reacties konden niet worden geladen.</p>
				<button type="button" class="uk-button uk-button-default" @click="loadContent">Opnieuw proberen</button>
			</div>
			<div v-show="loading" class="uk-margin-top"><loading-indicator v-show="loading" /></div>
			<div ref="commentsContent" v-html="htmlData"></div>
		</div>
	</div>
</template>

<script>

import axios from 'axios';
import { useId } from 'vue';
import LoadingIndicator from './LoadingIndicator.vue'

export default {
	components: { LoadingIndicator },
	setup() {
		return { commentsInstanceId: `comments-${useId()}` };
	},
  props: {
		url: {
			type: String,
			required: true
		},
		layout: {
			type: String,
			default: 'article',
		},
		totalComments: {
			type: Number,
			required: false
		},
		params: {
			type: Object,
			default: {}
		},
    buttonText: {
      type: String,
      required: false
    },
		targetElement: {
			type: String,
			required: false
		},
		jsVariables: {
			type: Object,
			required: true
		},
		autoLoad: {
			type: Boolean,
			default: false,
			required: false
		}
  },
	data() {
		return {
			hideText: false,
			loading: false,
			loaded: false,
			error: false,
			requestController: null,
			htmlData: null,
			commentScrollFrame: null,
			commentScrollTimer: null,
			commentHighlightTimer: null,
		}
	},
	mounted() {
		window.addEventListener('hashchange', this.handleCommentHash);

		var component = this;
		if (component.autoLoad) {
			component.$logger('app/components/comments-loader/autoload');
			component.loadContent();
		}
	},
	beforeUnmount() {
		this.requestController?.abort();
		window.removeEventListener('hashchange', this.handleCommentHash);

		if (this.commentScrollFrame) {
			window.cancelAnimationFrame(this.commentScrollFrame);
		}

		if (this.commentScrollTimer) {
			window.clearTimeout(this.commentScrollTimer);
		}

		if (this.commentHighlightTimer) {
			window.clearTimeout(this.commentHighlightTimer);
		}
	},
  methods: {
		handleCommentHash(event) {
			let hash = '';

			try {
				hash = new URL(event.newURL).hash;
			} catch (error) {
				hash = window.location.hash;
			}

			if (!/^#comment-\d+$/.test(hash)) {
				return;
			}

			const comment = document.getElementById(hash.slice(1));

			if (!comment || !this.$el.contains(comment)) {
				return;
			}

			const article = comment.closest('[data-article-stream-url]');
			const articleUrl = article ? article.dataset.articleStreamUrl : null;

			if (articleUrl && window.history && typeof window.history.replaceState === 'function') {
				const targetUrl = new URL(articleUrl, window.location.origin);

				window.history.replaceState(
					window.history.state,
					'',
					`${targetUrl.pathname}${targetUrl.search}`,
				);
			}

			if (this.commentScrollFrame) {
				window.cancelAnimationFrame(this.commentScrollFrame);
			}

			if (this.commentScrollTimer) {
				window.clearTimeout(this.commentScrollTimer);
			}

			this.highlightComment(comment);

			this.commentScrollFrame = window.requestAnimationFrame(() => {
				this.commentScrollFrame = null;
				this.scrollCommentIntoView(comment);

				// The sticky navigation animates into place after scrolling upward.
				this.commentScrollTimer = window.setTimeout(() => {
					this.commentScrollTimer = null;
					this.scrollCommentIntoView(comment);
				}, 550);
			});
		},
		scrollCommentIntoView(comment) {
			const headerOffset = ['.topbar', '.categorybar'].reduce((offset, selector) => {
				const element = document.querySelector(selector);

				return element ? offset + element.offsetHeight : offset;
			}, 0);
			const bounds = comment.getBoundingClientRect();
			const viewportTop = headerOffset + 32;
			const viewportBottom = window.innerHeight - 24;

			if (bounds.top < viewportTop || bounds.bottom > viewportBottom) {
				window.scrollTo({
					top: Math.max(0, window.scrollY + bounds.top - viewportTop),
					behavior: 'auto',
				});
			}
		},
		highlightComment(comment) {
			if (this.commentHighlightTimer) {
				window.clearTimeout(this.commentHighlightTimer);
			}

			comment.classList.remove('is-new-comment-highlight');
			void comment.offsetWidth;
			comment.classList.add('is-new-comment-highlight');

			this.commentHighlightTimer = window.setTimeout(() => {
				comment.classList.remove('is-new-comment-highlight');
				this.commentHighlightTimer = null;
			}, 3200);
		},
    handleClick() {
			this.hideText = true;
			this.loadContent();
    },
		async loadContent() {
			if (this.loading || this.loaded) return;

			var component = this;

			component.$logger('app/components/comments-loader/loadContent');
			component.$logger('app/components/comments-loader/loadContent/url:' + component.url);
			
			component.loading = true;
			component.error = false;
			const controller = new AbortController();
			component.requestController = controller;

			try {
				const response = await axios({
					method: 'get',
					url: component.url,
					params: component.params,
					signal: controller.signal,
				});
				if (controller.signal.aborted) return;
				
				if(component.targetElement) {
					document.getElementById(component.targetElement).innerHTML = response.data;
				} else {				
					component.htmlData = response.data;
				}	

				await component.$nextTick();
				if (controller.signal.aborted) return;
				const content = component.targetElement
					? document.getElementById(component.targetElement)
					: component.$refs.commentsContent;
				const comments = content.querySelector(component.jsVariables.id);
				if (comments) {
					// The article and drawer can render the same thread. The plugin looks up
					// its container globally, so each rendered copy needs its own selector.
					comments.id = component.commentsInstanceId;
					new Comments.Instance(`#${comments.id}`, component.jsVariables.settings);
				}
				component.loaded = true;
			} catch (e) {
				if (!controller.signal.aborted) {
					component.error = true;
					console.error(e);
				}
			} finally {
				component.loading = false;
				component.requestController = null;
			}

		}
  }
};
</script>

<style scoped>
</style>
